import { useState } from "react";
import MultiSelectDropdown from "./MultiSelectDropdown";
import CustomInput from "./CustomInput";
import DatePickerComponent from "./DatePickerComponent";
import CappingControl from "./CappingControl";
import {
  Button,
  ChakraProvider,
  InputGroup,
  InputLeftAddon,
} from "@chakra-ui/react";
import {
  campaignType,
  connectionTypes,
  contexts,
  pricingModels,
  targetDays,
  targetDevice,
} from "./data";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  CREATE_DIGITAL_AD,
  CREATE_DRAFTS,
} from "../../components/GraphQL/Mutation";
import mixpanel from "mixpanel-browser";
import { AUTH_TOKEN } from "../../constant";
import { useNavigate } from "react-router-dom";
import {
  adPayloadAtom,
  isRedirectingAfterLoginAtom,
} from "../../atom/digitalAdPayloadAtom";
import { useAtom } from "jotai";
import { useCreateDigitalBookingsDraft } from "../../hook";
import { TableSpinner } from "../../components/Spinner";
import { allowedDimensions } from "../../utils/constant";
import DigitalUploadFile from "../../components/UploadFile/DigitalUploadFile";

const CreateDigitalAd = ({ combinedSchema }) => {
  const [fileInfo, setFileInfo] = useState(null);
  const [errors, setErrors] = useState({});
  const [createDraftLoading, setCreateDraftLoading] = useState(false);
  const [payload, setPayload] = useAtom(adPayloadAtom);
  const { loading, createDigitalDraft } = useCreateDigitalBookingsDraft();
  const [isRedirectingAfterLogin, setIsRedirectingAfterLogin] = useAtom(
    isRedirectingAfterLoginAtom
  );

  const [mutate] = useMutation(CREATE_DRAFTS);

  const navigate = useNavigate();

  const allowedImageTypes = ["image/png", "image/gif"];
  const allowedVideoTypes = ["video/mp4"];

  const campaignTypeKey = Object.keys(campaignType).find(
    (key) => campaignType[key] === payload.campaignType
  );

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size
    if (file.size > 20 * 1024 * 1024) {
      alert("File size exceeds 20MB limit!");
      return;
    }

    // Validate image dimensions
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const width = img.width;
      const height = img.height;
      const dimensions = `${width}x${height}`;

      if (!allowedDimensions.includes(dimensions)) {
        alert(
          `Invalid dimensions! Allowed dimensions are: ${allowedDimensions.join(
            ", "
          )}`
        );
        return;
      }

      // If dimensions are valid, set file info and payload
      setFileInfo({ name: file.name, type: file.type });

      setPayload((prev) => ({
        ...prev,
        adCreativeTitle: file.name,
        adCreativeUrl: URL.createObjectURL(file), // Temporary preview URL
      }));
    };

    img.onerror = () => {
      alert("Failed to load image. Please upload a valid image file.");
    };
  };

  const handleSubmit = async () => {
    setCreateDraftLoading(true);
    try {
      combinedSchema.parse(payload); // Validate the entire payload
      setErrors({}); // Clear errors if validation passes

      // Proceed with submission
      const token = localStorage.getItem(AUTH_TOKEN);
      localStorage.setItem("digital-booking", JSON.stringify(payload));
      if (!token) {
        setIsRedirectingAfterLogin(true);
        return navigate("/auth/login");
      }

      createDigitalDraft(payload);
    } catch (error) {
      const validationErrors = {};
      console.log(error);
      error.errors.forEach((err) => {
        validationErrors[err.path[0]] = err.message;
      });
      setErrors(validationErrors); // Set errors for invalid fields
    } finally {
      setCreateDraftLoading(false);
    }
  };

  return (
    <ChakraProvider>
      <div className="pre-booking-campaign-second-tab-wrapper">
        <div className="pre-campaign-input-wrapper">
          <div className="pre-booking-multi-select-main-wrapper">
            <div className="pre-booking-multi-first-wrapper">
              <div className="pre-booking-multi-select-header">
                <p>AD Details</p>
              </div>
              <hr className="hr-bar" />
              <div className="pre-booking-campaign-details-wrapper-three">
                <div className="pre-booking-campaign-details-input">
                  <CustomInput
                    label="Ad Title"
                    type="text"
                    value={payload?.adTitle}
                    setValue={(value) =>
                      setPayload({ ...payload, adTitle: value })
                    }
                    required={true}
                    isError={!!errors.adTitle}
                    errorMessage={errors.adTitle || ""}
                  />
                </div>
                <div className="pre-booking-campaign-details-input">
                  <CustomInput
                    label="Company Names"
                    type="text"
                    value={payload?.companyNames}
                    setValue={(value) =>
                      setPayload({ ...payload, companyNames: value })
                    }
                    required={true}
                    isError={!!errors.companyNames}
                    errorMessage={errors.companyNames || ""}
                  />
                </div>
                <div className="pre-booking-campaign-details-input">
                  <InputGroup
                    size="sm"
                    display={"flex"}
                    alignItems={"baseline"}
                  >
                    <select
                      className="daily-currency-dropdown"
                      value={payload.currency}
                      onChange={(e) =>
                        setPayload({
                          ...payload,
                          currency: e.target.value,
                        })
                      }
                    >
                      <option value="USD">USD</option>
                      <option value="NGN">NGN</option>
                    </select>
                    <CustomInput
                      label="Daily Amount"
                      type="number"
                      name="dailyamount"
                      value={payload?.dailyamount}
                      setValue={(value) =>
                        setPayload({ ...payload, dailyamount: Number(value) })
                      }
                      required={true}
                      isError={!!errors.dailyamount}
                      errorMessage={errors.dailyamount || ""}
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="pre-booking-campaign-details-wrapper-three">
                <div className="pre-booking-campaign-details-input">
                  <DatePickerComponent
                    label="Start Date"
                    required={true}
                    selectedDate={payload?.startDate}
                    setSelectedDate={(value) =>
                      setPayload({ ...payload, startDate: value })
                    }
                    isError={!!errors.startDate}
                    errorMessage={errors.startDate || ""}
                  />
                </div>
                <div className="pre-booking-campaign-details-input">
                  <DatePickerComponent
                    label="End Date"
                    required={true}
                    selectedDate={payload?.endDate}
                    setSelectedDate={(value) =>
                      setPayload({ ...payload, endDate: value })
                    }
                    isError={!!errors.endDate}
                    errorMessage={errors.endDate || ""}
                  />
                </div>
                <div className="pre-booking-campaign-details-input">
                  <CustomInput
                    label="Ad Description"
                    type="text"
                    value={payload?.descriptionAd}
                    setValue={(value) =>
                      setPayload({ ...payload, descriptionAd: value })
                    }
                    required={true}
                    isError={!!errors.descriptionAd}
                    errorMessage={errors.descriptionAd || ""}
                  />
                </div>
                <div className="pre-booking-campaign-details-input">
                  <CustomInput
                    label="Target URL"
                    type="text"
                    value={payload?.targetUrl}
                    setValue={(value) =>
                      setPayload({ ...payload, targetUrl: value })
                    }
                    placeholder="https://example.com"
                    required={true}
                    isError={!!errors.targetUrl}
                    errorMessage={errors.targetUrl || ""}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="pre-booking-multi-select-main-wrapper">
            <div className="pre-booking-multi-first-wrapper">
              <div className="pre-booking-multi-select-header">
                <p>Ad Creatives</p>
              </div>
              <hr className="hr-bar" />
              <div className="pre-booking-campaign-details-wrapper">
                <div className="pre-booking-campaign-details-input">
                  <CustomInput
                    label="Title"
                    type="text"
                    value={payload?.creativeTitle}
                    setValue={(value) =>
                      setPayload({ ...payload, creativeTitle: value })
                    }
                    required={true}
                    isError={!!errors.creativeTitle}
                    errorMessage={errors.creativeTitle || ""}
                  />
                </div>
                <div className="pre-booking-campaign-details-input">
                  <CustomInput
                    label="Description"
                    type="text"
                    value={payload?.creativeDescription}
                    setValue={(value) =>
                      setPayload({ ...payload, creativeDescription: value })
                    }
                    required={true}
                    isError={!!errors.creativeDescription}
                    errorMessage={errors.creativeDescription || ""}
                  />
                </div>
              </div>
              <div className="pre-booking-campaign-details-input">
                <CustomInput
                  label="Ad URL"
                  type="text"
                  value={payload?.creativeUrl}
                  setValue={(value) =>
                    setPayload({ ...payload, creativeUrl: value })
                  }
                  placeholder="https://example.com"
                  required={true}
                  isError={!!errors.creativeUrl}
                  errorMessage={errors.creativeUrl || ""}
                />
              </div>
              <div className="pre-booking-campaign-details-wrapper-single">
                {/* Push Notification (Two separate uploads) */}
                {campaignTypeKey === "1" && (
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      gap: "30px",
                      alignItems: "center",
                    }}
                  >
                    <DigitalUploadFile
                      label="Upload Creative Icon"
                      accept={allowedImageTypes.join(",")}
                      handleFileUpload={handleFileUpload}
                      allowedDimensions={["192x192"]}
                      uploadFileType="iconFile"
                    />
                    <DigitalUploadFile
                      label="Upload Creative Asset (Multiple Dimensions)"
                      accept={allowedImageTypes.join(",")}
                      handleFileUpload={handleFileUpload}
                      allowedDimensions={[
                        "900x600",
                        "720x480",
                        "480x320",
                        "360x240",
                        "600x400",
                      ]}
                      uploadFileType="imageFile"
                    />
                  </div>
                )}

                {/* Banner Ad / Display Ad */}
                {campaignTypeKey === "4" && (
                  <DigitalUploadFile
                    label="Upload Creative Asset"
                    accept={allowedImageTypes.join(",")}
                    handleFileUpload={handleFileUpload}
                    allowedDimensions={[
                      "120x60",
                      "300x250",
                      "320x50",
                      "728x90",
                      "320x100",
                      "300x100",
                      "970x90",
                      "320x480",
                      "300x50",
                      "970x250",
                      "468x60",
                      "160x600",
                      "900x600",
                      "720x480",
                      "600x400",
                      "600x500",
                      "360x240",
                      "350x240",
                      "1000x1000",
                      "480x320",
                    ]}
                    uploadFileType="imageFile"
                  />
                )}

                {/* Video Ad */}
                {(campaignTypeKey === "5" || campaignTypeKey === "10") && (
                  <DigitalUploadFile
                    label="Upload Video Ad"
                    accept={allowedVideoTypes.join(",")}
                    handleFileUpload={handleFileUpload}
                    allowedDimensions={[
                      "300x250",
                      "320x50",
                      "728x90",
                      "320x100",
                      "300x100",
                      " 970x90",
                      "320x480",
                      "300x50",
                      "970x250",
                      "468x60",
                      "160x600",
                      "900x600",
                      "720x480",
                      "600x400",
                      "600x500",
                      "360x240",
                      "350x240",
                      "1000x1000",
                      "480x320",
                    ]}
                    uploadFileType="videoFile"
                  />
                )}

                {/* No file upload required for Pop Ad */}
              </div>
            </div>
          </div>

          <div className="pre-booking-campaign-submit-button-footer">
            <Button
              className="pre-booking-campaign-submit-button"
              //   disabled={!isFormValid}
              onClick={() => handleSubmit()}
            >
              {loading ? (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <i class="fa fa-circle-o-notch fa-spin"></i>
                  <p>Creating</p>
                </div>
              ) : (
                <>Create Ad</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </ChakraProvider>
  );
};

export default CreateDigitalAd;
